// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext } from 'react';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THForm } from '@Molecules/THForm/THForm';
import { CustomFieldValueOutput } from 'thehive-sdk';
import { normalizeDate } from '@Helpers/date';
import { Space } from 'antd';
import { ICONS } from '@Constants/iconsApp';
import DeleteButton from '@Atoms/Buttons/DeleteButton/DeleteButton';
import { useLocalMessage } from '@Helpers/locale';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { DefaultOptionType } from 'antd/lib/select';
import { ScopeContext } from '@Context/ScopeProvider';
import { classList } from '@Helpers/className';
import _ from 'lodash';
import { THInputLink } from '@Molecules/Inputs/THInputLink/THInputLink';
import { urlValidator } from '@Helpers/string';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';

import './CFValueItem.scss';

interface InputOptions {
    borderless?: boolean;
    disabled?: boolean;
    placeholder?: string;
    options: DefaultOptionType[];
}

interface CFValueItemProps {
    customField: CustomFieldValueOutput;
    fieldName: number;
    onDelete?: () => void;
    borderless?: boolean;
    disabled?: boolean;
    mandatory?: boolean;
    showLabel?: boolean;
    hidden?: boolean;
}

export const CFValueItem = ({
    customField,
    fieldName,
    onDelete,
    borderless = true,
    disabled = false,
    mandatory,
    showLabel = true,
    hidden = false,
}: CFValueItemProps): ReactElement => {
    const { name, type = 'default', description = '' } = customField;
    const i = useLocalMessage();

    const { customFieldList: customFieldsInfos } = useContext(ScopeContext);

    const cf = customFieldsInfos?.find(({ name: cfName }) => cfName === name);
    const { options = [], displayName } = cf || {};
    const mapInputToType: Record<
        CustomFieldValueOutput['type'] | 'options' | 'default',
        (props: InputOptions) => ReactElement
    > = {
        boolean: ({ borderless, disabled, placeholder, options }) =>
            typeof customField.value !== 'boolean' ? (
                <THSelect
                    options={options}
                    borderless={borderless}
                    disabled={disabled}
                    placeholder={placeholder}
                    allowClear
                    showSearch
                />
            ) : (
                <THSwitch disabled={disabled} />
            ),
        date: ({ borderless, disabled, placeholder }) => (
            <THDatePicker borderless={borderless} disabled={disabled} placeholder={placeholder} allowClear />
        ),
        integer: ({ borderless, disabled, placeholder }) => (
            <THInputNumber
                borderless={borderless}
                disabled={disabled}
                placeholder={placeholder}
                className="CFValueItem__Number"
            />
        ),
        float: ({ borderless, disabled, placeholder }) => (
            <THInputNumber borderless={borderless} disabled={disabled} placeholder={placeholder} />
        ),
        string: ({ borderless, disabled, placeholder }) => (
            <THInput borderless={borderless} disabled={disabled} placeholder={placeholder} />
        ),
        url: ({ borderless, disabled, placeholder }) => (
            <THInputLink borderless={borderless} disabled={disabled} placeholder={placeholder} />
        ),
        options: ({ borderless, disabled, options, placeholder }) => (
            <THAutoComplete
                options={options}
                borderless={borderless}
                disabled={disabled}
                placeholder={placeholder}
                filtered
                allowClear
            />
        ),
        default: ({ borderless, disabled, placeholder }) => (
            <THInput borderless={borderless} disabled={disabled} placeholder={placeholder} allowClear />
        ),
    };

    return (
        customField && (
            <div className={classList('CFValueItem', { 'CFValueITem--hidden': hidden })}>
                <THForm.Item
                    className="CFValueItem__FormItem"
                    name={[fieldName, 'value']}
                    label={
                        showLabel && (
                            <Space>
                                {displayName || name}
                                <THTooltip title={description}>
                                    <ICONS.QuestionCircleOutlined />
                                </THTooltip>
                            </Space>
                        )
                    }
                    valuePropName={type === 'boolean' ? 'checked' : undefined}
                    normalize={type === 'date' ? normalizeDate : undefined}
                    rules={[
                        { required: mandatory, message: i('case.close.form.custom-field') },
                        {
                            validator: (__, value) => {
                                if (!value) {
                                    return Promise.resolve();
                                }

                                if (type === 'url') {
                                    return urlValidator(__, value);
                                }

                                if (_.isEmpty(options)) {
                                    return Promise.resolve();
                                }

                                if (!options.includes(value)) {
                                    return Promise.reject(i('customField.value.invalid'));
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    {mapInputToType[options?.length ? 'options' : type]({
                        borderless,
                        disabled,
                        placeholder: i('common.value-placeholder'),
                        options:
                            type === 'boolean'
                                ? [
                                      { label: 'true', value: 'true' },
                                      { label: 'false', value: 'false' },
                                  ]
                                : options.map((value) => ({ label: value, value })),
                    })}
                </THForm.Item>

                {onDelete && !disabled && <DeleteButton className="CFValueItem__Delete" onClick={onDelete} />}
            </div>
        )
    );
};
